import {inject, injectable} from 'inversify';
import {FullscreenView} from '@fantasmagames/core/src/screen/fullscreen/FullscreenView';
import {Device} from '@fantasmagames/core/src/device/Device';
import {System} from '@fantasmagames/core/src/System';
import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {CoreDI} from '@fantasmagames/core/src/di/CoreDI';
import {DefaultFullscreenView} from '@fantasmagames/core/src/screen/fullscreen/DefaultFullscreenView';
import {ScreenEvents} from '@fantasmagames/core/src/screen/ScreenEvents';
import {ScreenParams} from '@fantasmagames/core/src/screen/fullscreen/ScreenParams';
import {IOSFullScreenView} from '@fantasmagames/core/src/screen/fullscreen/IOSFullScreenView';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {HTMLCreationUtils} from '@fantasmagames/core/src/html/HTMLCreationUtils';

export enum ScreenOrientation {
	PORTRAIT,
	LANDSCAPE,
}

@injectable()
export class FullscreenManager {
	private _orientation: ScreenOrientation;
	private _view: FullscreenView;
	private _canvas: HTMLCanvasElement;
	private _device: Device;
	private _app: PIXI.Application;
	private _system: System;
	private _width: number;
	private _height: number;
	private _canvasWidth: number;
	private _canvasHeight: number;
	private _eventManager: IEventManagerWithAsync;

	private _currentOrientation: ScreenOrientation;
	private _currentWidth: number;
	private _currentHeight: number;
	private _currentOuterWidth: number;
	private _currentOuterHeight: number;

	/**
	 * flag for iOS safari bug when semi-fullscreen shows after rotation
	 */
	private _hasIOSBug: boolean = false;
	private _oldFullScreenStatus: boolean = false;

	private _previousFullscreen: boolean = false;

	constructor(
		@inject(CoreDI.Device) device: Device,
		@inject(CoreDI.Application) app: PIXI.Application,
		@inject(CoreDI.System) system: System,
		@inject(CoreDI.IEventManagerWithAsync) eventManager: IEventManagerWithAsync,
	) {
		this._device = device;
		this._app = app;
		this._system = system;
		this._eventManager = eventManager;
		this._canvas = this._app.view;

		window.addEventListener('resize', this.onOrientationChanged.bind(this));

		this.setCurrentParameters();
		this.screenUpdateCallback();

		if (this._device.mobileSafari) {
			this._view = new IOSFullScreenView();
		} else if (this._device.fullscreen) {
			this._view = new DefaultFullscreenView();

			if (!this._device.desktop) {
				document.body.addEventListener('touchend', this.onElementClicked.bind(this));
			}
		}

		if (!this._device.isIframe && !this._device.isFullscreenDisabledByIntegration && this._device.android) {
			this._previousFullscreen = this.isFullscreen();
		}

		this.setOrientationFromWindowParams();
		this.resizeCanvas();
	}

	protected onFullscreenChange(): void {
		if (this.isFullscreen() !== this._previousFullscreen) {
			this._eventManager.emit(ScreenEvents.FULLSCREEN_STATUS_CHANGED);

			this.isFullscreen()
				? this._eventManager.emit(ScreenEvents.FULLSCREEN_ENTERED)
				: this._eventManager.emit(ScreenEvents.FULLSCREEN_EXITED);
		}

		this._previousFullscreen = this.isFullscreen();
	}

	private setCurrentParameters(): void {
		this._currentWidth = window.innerWidth;
		this._currentHeight = window.innerHeight;
		this._currentOuterHeight = window.outerHeight;
		this._currentOuterWidth = window.outerWidth;
		this._currentOrientation = this.getOrientation();
	}

	private screenUpdateCallback(): void {
		// update orientation if it was changed or there is a special IOS bug
		if (this._currentOrientation !== this.getOrientation() || this._hasIOSBug) {
			this.onOrientationChanged();
		}

		// updating size of the screen if window size was changed or there is a special IOS bug
		if (window.innerWidth !== this._currentWidth || window.innerHeight !== this._currentHeight || this._hasIOSBug) {
			this.resize();
		}

		// if we have an iframe and window size changed
		if (this._device.isIframe && (window.outerWidth !== this._currentOuterWidth || window.outerHeight !== this._currentOuterHeight)) {
			this.onOrientationChanged();

			this._currentOuterHeight = window.outerHeight;
			this._currentOuterWidth = window.outerWidth;
		}

		// repeating this check every 100 ms
		setTimeout(() => {
			this.screenUpdateCallback();
		}, 100);
	}

	public forceUpdate(): void {
		this.onOrientationChanged();
	}

	public windowPositionToCanvas(windowPosition: Position): Position {
		const ratio = this._system.resolutionManager.resolution.width / this._canvasWidth;

		const position: Position = {x: windowPosition.x * ratio, y: windowPosition.y * ratio};

		return position;
	}

	public canvasPositionToWindow(canvasPosition: Position): Position {
		const ratio = this._canvasWidth / this._system.resolutionManager.resolution.width;

		const position: Position = {x: canvasPosition.x * ratio, y: canvasPosition.y * ratio};

		return position;
	}

	protected canGoToFullscreen(): boolean {
		return !this.isFullscreen() && !this._device.isFullscreenDisabledByIntegration && !this._device.isIframe;
	}

	private onElementClicked(): void {
		// Blocking fullscreen on iframe. Because Dervico said so?
		if (this.canGoToFullscreen()) {
			document.body[this._device.requestFullscreen]();

			setTimeout(() => {
				this.onFullscreenChange();
				if (document.getElementById('viewporter')) {
					document.getElementById('viewporter').style.height = `${window.innerHeight}px`;
					document.documentElement.style.minHeight = `${window.innerHeight}px`;
				}
			}, 500);
		}
	}

	private isFullscreen(): boolean {
		return !!document[this._device.fullscreenElement];
	}

	private onResized(): void {
		this._view.handle(this._orientation);
		this.resizeCanvas();

		const params: ScreenParams = {
			width: this._width,
			height: this._height,
			orientation: this._orientation,
		};

		this._eventManager.emit(ScreenEvents.SCREEN_RESIZED, params);
	}

	private resize(): void {
		this._view && this._view.handle(this._orientation);
		this.resizeCanvas();

		const params: ScreenParams = {
			width: this._width,
			height: this._height,
			orientation: this._orientation,
		};

		this._currentWidth = window.innerWidth;
		this._currentHeight = window.innerHeight;

		this._eventManager.emit(ScreenEvents.SCREEN_RESIZED, params);
	}

	private resizeCanvas(): void {
		if (!this._device.desktop) {
			if (this._orientation === ScreenOrientation.LANDSCAPE) {
				this._width = this._system.viewport.width;
				this._height = this._system.viewport.height;
			} else {
				this._width = this._system.viewport.height;
				this._height = this._system.viewport.width; // * window.innerHeight / window.innerWidth;
			}

			this._app.renderer.resize(this._width, this._height);
		} else {
			this._width = this._system.viewport.width;
			this._height = this._system.viewport.height;

			this._canvas.style.top = '0px';
			this._canvas.style.left = '0px';
		}

		const ratio = this._canvas.width / this._canvas.height;

		let w: number;
		let h: number;

		let innerWidth = 0;
		let innerHeight = 0;

		innerWidth = window.innerWidth;
		innerHeight = window.innerHeight;

		if (this._device.desktop /*|| this._orientation === ScreenOrientation.LANDSCAPE*/) {
			if (innerWidth / innerHeight >= ratio) {
				w = innerHeight * ratio;
				h = innerHeight;
			} else {
				w = innerWidth;
				h = innerWidth / ratio;
			}
		} else {
			if (!this._device.iOS) {
				if (!this._device.isIframe) {
					innerWidth = window.outerWidth;
					innerHeight = window.outerHeight;
				}
			}

			const dpr = window.devicePixelRatio;
			const innerRatio = innerWidth / innerHeight;

			if (innerRatio > ratio) {
				w = innerWidth * (ratio / innerRatio);
				h = innerHeight;

				const marginLeft = (innerWidth - w) / 2;
				const marginTop = (innerHeight - h) / 2;

				this._canvas.style.marginLeft = Math.ceil(marginLeft) + 'px';
				this._canvas.style.marginTop = Math.ceil(marginTop) + 'px';
				if (this._hasIOSBug && this._canvas.getBoundingClientRect().top !== 0) {
					this._canvas.style.marginTop = Math.ceil(marginTop - this._canvas.getBoundingClientRect().top) + 'px';
					this._hasIOSBug = false;
				}
			} else {
				w = innerWidth;
				h = innerHeight * (innerRatio / ratio);

				const marginLeft = (innerWidth - w) / 2;
				const marginTop = (innerHeight - h) / 2;

				this._canvas.style.marginLeft = Math.ceil(marginLeft) + 'px';
				this._canvas.style.marginTop = Math.ceil(marginTop) + 'px';
				if (this._hasIOSBug && this._canvas.getBoundingClientRect().top !== 0) {
					this._canvas.style.marginTop = Math.ceil(marginTop - this._canvas.getBoundingClientRect().top) + 'px';
					this._hasIOSBug = false;
				}
			}
		}

		this._canvas.style.width = Math.ceil(w) + 'px';
		this._canvas.style.height = Math.ceil(h) + 'px';

		this._canvasWidth = w;
		this._canvasHeight = h;
	}

	private onOrientationChanged(): void {
		// special delays for different kind of devices
		let delay = this._device.iOS || this._device.isIOSMobileRequestingDesktop ? 200 : 0;
		delay = this._device.isSamsungNative ? 400 : delay;
		this._hasIOSBug = false;

		if (!this._device.isIframe && !this._device.isFullscreenDisabledByIntegration && this._device.android) {
			this.onFullscreenChange();
		}

		setTimeout(() => {
			const orientBefore = this._orientation;
			this.setOrientationFromWindowParams();

			if (orientBefore !== this._orientation) {
				this.updateScreenBasedOnOrientation();
			} else {
				if (this._device.iOS && this._device.mobileSafari) {
					if (this._oldFullScreenStatus && !this.isFullscreenIOS(this._orientation)) {
						this._hasIOSBug = true;
					}
				}

				this.updateScreenBasedOnOrientation();
			}
			this._currentOrientation = this._orientation;
			this._oldFullScreenStatus = this.isFullscreenIOS(this._orientation);
		}, delay);
	}

	private isFullscreenIOS(orientation: ScreenOrientation): boolean {
		if (orientation === ScreenOrientation.LANDSCAPE) {
			// for safari "window.innerHeight >= screen.width" is enough but for chrome we need to
			// have 20px navbar;
			const barHeight = 44;

			return window.innerHeight + barHeight >= screen.width;
		} else {
			const barHeight = 64;

			return window.innerHeight + barHeight >= screen.height;
		}
	}

	protected setOrientationFromWindowParams(): void {
		if (this._device.iOS && this._device.isMobile && window.hasOwnProperty('orientation')) {
			if (window.orientation === 90 || window.orientation === -90) {
				this._orientation = ScreenOrientation.LANDSCAPE;
			} else {
				this._orientation = ScreenOrientation.PORTRAIT;
			}
		} else {
			if (window.innerHeight <= window.innerWidth) {
				this._orientation = ScreenOrientation.LANDSCAPE;
			} else {
				this._orientation = ScreenOrientation.PORTRAIT;
			}
		}
	}

	private getOrientation(): ScreenOrientation {
		if (this._device.iOS && this._device.isMobile && window.hasOwnProperty('orientation')) {
			if (window.orientation === 90 || window.orientation === -90) {
				return ScreenOrientation.LANDSCAPE;
			} else {
				return ScreenOrientation.PORTRAIT;
			}
		} else {
			if (window.innerHeight <= window.innerWidth) {
				return ScreenOrientation.LANDSCAPE;
			} else {
				return ScreenOrientation.PORTRAIT;
			}
		}
	}

	protected updateScreenBasedOnOrientation(): void {
		if (this._device.isMobile) {
			if (this._orientation === ScreenOrientation.LANDSCAPE) {
				this._eventManager.emit(ScreenEvents.LANDSCAPE_MODE);
			} else {
				this._eventManager.emit(ScreenEvents.PORTRAIT_MODE);
			}

			this._eventManager.emit(ScreenEvents.ORIENTATION_CHANGED, this._orientation);
		}

		this.onResized();
	}

	protected fullscreenDisabledByIntegration(isDisabled: boolean): void {
		this._device.fullscreenDisabledByIntegration = isDisabled;
	}

	public get orientation(): ScreenOrientation {
		return this._orientation;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/core/src/screen/fullscreen/FullscreenManager.ts